import moment from "moment";
import { isTimestampFromPastDay } from "../../../shared/utils/playbackHelpers";
import { shakaSetVideoPlayerControlState } from "./shakaPlayerControls";
import { shakaGetHlsLivePlayheadTs } from "./playerCoreUtils";
import PlayerConstants from "../../../shared/constants/player";
import { getCurrentTimestamp } from "../core/playerUtils";

const { PLAYBACK_FORWARD_BUFFER, PLAYER_CONTROLS } = PlayerConstants;

export const playerCheckIsNotAtLivePosition = (playerMgr, isRestartedStream, isHls) => {
  // variable to store current playback time
  const playHead = isHls
    ? !playerMgr.current?.destroyed && shakaGetHlsLivePlayheadTs(playerMgr)
    : !playerMgr.current?.destroyed && playerMgr.current?.getCurrentTime();
  /*
    For unknown reason, HLS stream returns non-unix-timestamp random numbers at the beginning of segments loading, which causes "jump to live" button to display for a split second.
    So a check by invoking isTimestampFromPastDay function will ensure the jump-to-live button won't show up in Safari for a normal PLTV live stream.(Addresses TSQA-20704)
  */
  if (isTimestampFromPastDay(playHead) && !isRestartedStream) return false;
  const liveEdgeThreshold = isRestartedStream ? 5 : 55;
  if (playHead === undefined || playHead === null) return false;
  return isHls
    ? Math.abs(playHead / 1000 - (moment().valueOf() - PLAYBACK_FORWARD_BUFFER) / 1000) > liveEdgeThreshold
    : Math.abs(playHead - (moment().valueOf() - PLAYBACK_FORWARD_BUFFER) / 1000) > liveEdgeThreshold;
};

export const shakaHlsLiveTsToSeekPoint = (playerMgr, targetTimeStamp) => {
  const seekRange = playerMgr.current.getSeekRange();
  const currentPos = playerMgr.current.getCurrentTime();
  const currentPosTs = shakaGetHlsLivePlayheadTs(playerMgr);
  const offset = (targetTimeStamp - currentPosTs) / 1000;
  let seekPoint = currentPos + offset;
  if (seekPoint < seekRange.start) {
    seekPoint = seekRange.start;
  } else if (seekPoint > seekRange.end) {
    seekPoint = seekRange.end;
  }
  return seekPoint;
};

export const playerHandleDynamicSeeking = (
  playerMgr,
  newPos,
  seekTarget,
  landTime,
  appendCurrentPos,
  displayToastNotification,
  isSafari,
  shakaHlsLandTimeSeekHead,
  recordingStartDeltaTime,
  isRecordingInProgress,
  userRecordingStartTimeRef
) => {
  if (seekTarget > 0 && Math.abs(newPos - (moment().valueOf() - PLAYBACK_FORWARD_BUFFER) / 1000) < 50) {
    playerMgr.current.jumpToLive();
    displayToastNotification("live_position_reached");
  } else if (newPos < landTime.current / 1000) {
    isSafari
      ? playerMgr.current.timeShift(landTime.current / 1000 + PLAYBACK_FORWARD_BUFFER) // hls streams add the buffer by default, so we need this adjustment
      : playerMgr.current.timeShift(landTime.current / 1000);
    displayToastNotification("beginning_of_available_stream_reached");
  } else {
    if (appendCurrentPos) {
      if (isSafari) {
        if (seekTarget < 0) {
          let rewindTo = playerMgr.current.getCurrentTime() + seekTarget;
          if (rewindTo < shakaHlsLandTimeSeekHead.current) {
            rewindTo = shakaHlsLandTimeSeekHead.current;
          }
          playerMgr.current.timeShift(rewindTo);
        } else if (seekTarget > 0) {
          let fastForwardTo = playerMgr.current.getCurrentTime() + seekTarget;
          if (fastForwardTo > playerMgr.current.getSeekRange().end) {
            fastForwardTo = playerMgr.current.getSeekRange().end;
            displayToastNotification("live_position_reached");
          }
          playerMgr.current.timeShift(fastForwardTo);
        }
      } else if (
        isRecordingInProgress &&
        seekTarget > 0 &&
        newPos >
          parseInt(
            (getCurrentTimestamp() - userRecordingStartTimeRef.current - PLAYBACK_FORWARD_BUFFER) / 1000 + seekTarget
          )
      ) {
        displayToastNotification("live_position_reached");
      } else {
        playerMgr.current.timeShift(newPos);
      }
    } else {
      playerMgr.current.timeShift(seekTarget - moment().valueOf());
    }
  }
};

export const playerHandleJumpToLive = (hasJumpedToLive, playerMgr, setPlayPauseIcon) => {
  hasJumpedToLive.current = true;
  if (!playerMgr.destroyed && playerMgr.current?.isPaused()) {
    setPlayPauseIcon("/images/playback-pause.svg");
    shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.RESUME);
  }
  playerMgr.current.jumpToLive();
};

export const playerIsLive = (playerMgr) => {
  return playerMgr.current?.isLive();
};

export const playerTimeShift = (playerMgr, time) => {
  playerMgr.current?.timeShift(time);
};

export const playerLiveStreamProgress = (playerMgr, startTime, endTime, landTime) => {
  return ((playerMgr?.current?.getCurrentTime() * 1000 - landTime.current) / (endTime - startTime)) * 100;
};

export const shakaHlsLiveProgress = (playerMgr, startTime, endTime, landTime) => {
  return ((shakaGetHlsLivePlayheadTs(playerMgr) - landTime.current) / (endTime - startTime)) * 100;
};

export const playerToggleLiveProgramUpdate = (
  isFast,
  liveProgramCanUpdate,
  playerMgr,
  endTime,
  isSafari,
  setTriggerSafariUpdate
) => {
  if (!isFast && liveProgramCanUpdate.current && playerMgr?.current?.getTimeShift() < 0) {
    liveProgramCanUpdate.current = false;
  } else if (
    !liveProgramCanUpdate.current &&
    ((playerMgr.current?.isLive() && playerMgr.current?.getTimeShift() === 0) ||
      (isSafari ? shakaGetHlsLivePlayheadTs(playerMgr) : playerMgr.current?.getCurrentTime() * 1000) >= endTime)
  ) {
    // trigger
    if (isSafari) {
      setTriggerSafariUpdate(true);
    } else {
      liveProgramCanUpdate.current = true;
    }
  }
};

export const playerHandleLiveProgressEnd = (
  isPauseLiveAllowed,
  liveProgressPercentage,
  onPlaybackStateChange,
  playerMgr,
  endTime,
  leftMargin,
  landTime,
  startTime,
  isSafari
) => {
  const currTime = isSafari ? shakaGetHlsLivePlayheadTs(playerMgr) : playerMgr?.current?.getCurrentTime();
  const currEndTime = isSafari ? endTime : endTime / 1000;
  if (!isPauseLiveAllowed.current && liveProgressPercentage >= 100) {
    onPlaybackStateChange(PLAYER_CONTROLS.GET_NEXT_LIVE_PROGRAM);
    liveProgressPercentage = 0;
  } else if (currTime >= currEndTime) {
    onPlaybackStateChange(PLAYER_CONTROLS.GET_NEXT_LIVE_PROGRAM);
    leftMargin.current = 0;
    landTime.current = (playerMgr?.current?.getCurrentTime() || 0) * 1000;
    liveProgressPercentage = ((endTime - landTime.current) / (endTime - startTime)) * 100;
  }

  return liveProgressPercentage;
};

export const playerCalculateLandTime = (pauseLiveProperties) => {
  return (
    ((getCurrentTimestamp() - PLAYBACK_FORWARD_BUFFER * 1000) / 1000 - pauseLiveProperties?.bufferThresholdMins * 60) *
    1000
  );
};

export const playerUpdateLiveProgress = (playerMgr, progress, isSafari) => {
  if (isSafari) {
    playerTimeShift(playerMgr, shakaHlsLiveTsToSeekPoint(playerMgr, progress * 1000));
  } else {
    playerTimeShift(playerMgr, progress);
  }
};

// We attempt to set hasNextProgramStarted.current = true to update the leftMargin but
// issue with this is that we can't ensure this is called in a timely manner that lines up with the endtime.
// Endtime could be updated before we reach the end of the current endTime.
export const playerOnNextLiveProgramStarted = (playerMgr, endTime, hasNextProgramStarted) => {
  if (playerMgr?.current?.getCurrentTime() >= endTime / 1000) {
    hasNextProgramStarted.current = true; // Current playhead passes program end time
  }
};
